import { Expiry } from '../types/expiry';

function parseIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isWeekend(d: Date): boolean {
  const wd = d.getDay();
  return wd === 0 || wd === 6;
}

function isTradingDay(d: Date, holidays: Record<string, string>): boolean {
  return !isWeekend(d) && holidays[toIso(d)] === undefined;
}

/**
 * Trading days in the half-open interval [from, to): `from` counts if it is
 * itself a trading day, `to` is excluded. The day before expiry → 1 DTE,
 * the expiry day itself → 0 DTE. Non-trading input dates (weekend/holiday)
 * naturally don't contribute to the count.
 */
function tradingDaysFromTo(fromIso: string, toIso: string, holidays: Record<string, string>): number {
  if (fromIso === toIso) return 0;
  const from = parseIso(fromIso);
  const to = parseIso(toIso);
  if (from > to) return -1;
  let count = 0;
  const cursor = new Date(from);
  while (cursor < to) {
    if (isTradingDay(cursor, holidays)) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

/**
 * For a given calendar date, find the smallest expiry on/after that date and
 * return its DTE (trading-days-to-expiry). 0 = expiry day itself, 1 = the
 * trading day immediately before expiry. Returns null if `date` is past every
 * fetched expiry.
 *
 * Note: `expiries` must be sorted ascending by expiryDate. `holidays` is the
 * map fetched from /reference-data?type=holidays — pass `{}` if none.
 */
export function computeDTE(
  date: string,
  expiries: Expiry[],
  holidays: Record<string, string> = {},
): number | null {
  for (const exp of expiries) {
    if (exp.expiryDate >= date) {
      if (exp.expiryDate === date) return 0;
      return tradingDaysFromTo(date, exp.expiryDate, holidays);
    }
  }
  return null;
}

/**
 * Build a Map<dateString, DTE> for a list of dates.
 */
export function buildDteMap(
  dates: string[],
  expiries: Expiry[],
  holidays: Record<string, string> = {},
): Map<string, number | null> {
  const out = new Map<string, number | null>();
  for (const d of dates) {
    out.set(d, computeDTE(d, expiries, holidays));
  }
  return out;
}